import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Input, Spin, Tag, message } from 'antd';
import { CustomerServiceOutlined, CloseOutlined, SendOutlined, RobotOutlined } from '@ant-design/icons';
import './index.css';
import { sophiaAssistantApi } from '../../api/sophiaassistant';

const QUICK_QUESTIONS = [
  'PAI 和 PMS 的定位区别是什么？',
  '如何在 WorkOrder 里快速筛选待办订单？',
  'NO CARGO RELEASE 和 DEMURRAGE 的提醒逻辑是什么？',
  '订单详情页里 Attachments / Emails / A/N 分别怎么用？',
];

const MAX_HISTORY_COUNT = 6;

function SophiaAssistant() {
  const [messageApi, contextHolder] = message.useMessage();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([
    { role: 'assistant', content: '你好，我是 PAI 内部客服 Sophia。你可以先点右侧快捷问题。' },
  ]);
  const messagesEndRef = useRef(null);

  const canSend = useMemo(() => input.trim().length > 0 && !loading, [input, loading]);

  const requestAI = async (question) => {
    const userMsg = { role: 'user', content: question };
    const nextMessages = [...messages, userMsg];
    setMessages(nextMessages);
    setLoading(true);
    try {
      const history = messages
        .filter((m) => ['user', 'assistant'].includes(m.role) && m.content)
        .slice(-MAX_HISTORY_COUNT)
        .map((m) => ({ role: m.role, content: m.content }));

      const res = await sophiaAssistantApi.chat({
        question,
        history,
      });

      const responseBody = res?.data || {};
      if (responseBody.code !== 200) {
        throw new Error(responseBody.message || 'Sophia service is unavailable');
      }

      const data = responseBody.data || {};
      const answer = data.answer
        || '收到你的问题了，但我暂时没有拿到有效回复，请稍后重试。';

      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: answer,
        citations: Array.isArray(data.citations) ? data.citations : [],
        model: data.model,
        totalDurationMs: data.totalDurationMs,
      }]);
    } catch (error) {
      messageApi.error(`Sophia 服务暂时不可用：${error?.response?.data?.message || error?.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    const q = input.trim();
    if (!q || loading) return;
    setInput('');
    await requestAI(q);
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, loading]);

  return (
    <div className="sophia-widget">
      {contextHolder}

      {open && (
        <div className="sophia-panel">
          <div className="sophia-header">
            <div className="sophia-title"><CustomerServiceOutlined /> PAI Internal Support - Sophia</div>
            <Button type="text" icon={<CloseOutlined />} onClick={() => setOpen(false)} />
          </div>

          <div className="sophia-main">
            <div className="sophia-left">
              <div className="sophia-messages">
                {messages.map((m, idx) => (
                  <div key={`${m.role}-${idx}`} className={`sophia-msg sophia-${m.role}`}>
                    <div>{m.content}</div>
                    {m.role === 'assistant' && m.citations?.length > 0 && (
                      <div className="sophia-citations">
                        {m.citations.map((citation) => (
                          <div key={citation.index} className="sophia-citation">
                            <span className="sophia-citation-index">[{citation.index}]</span>
                            <span className="sophia-citation-title">{citation.title || 'Reference'}</span>
                            {citation.excerpt ? (
                              <span className="sophia-citation-excerpt">{citation.excerpt}</span>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                {loading && (
                  <div className="sophia-loading"><Spin size="small" /> Sophia is thinking...</div>
                )}
                <div ref={messagesEndRef} />
              </div>

              <div className="sophia-input">
                <Input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onPressEnter={handleSend}
                  placeholder="Ask Sophia..."
                />
                <Button type="primary" icon={<SendOutlined />} onClick={handleSend} disabled={!canSend} />
              </div>
            </div>

            <div className="sophia-right">
              <div className="sophia-intro">
                <div className="sophia-intro-title">Sophia 简介</div>
                <div className="sophia-intro-text">
                  PAI 内部客服，帮助你快速理解 WorkOrder、提醒中心、A/N 与 PU 自动化规则。
                </div>
              </div>

              <div className="sophia-quick">
                {QUICK_QUESTIONS.map((q) => (
                  <Tag
                    key={q}
                    className={`sophia-quick-tag${loading ? ' disabled' : ''}`}
                    onClick={() => !loading && requestAI(q)}
                  >
                    {q}
                  </Tag>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {!open && (
        <Button
          type="primary"
          shape="circle"
          size="large"
          className="sophia-fab"
          icon={<RobotOutlined />}
          onClick={() => setOpen(true)}
        />
      )}
    </div>
  );
}

export default SophiaAssistant;
